/**
 * Recording a voice note in the composer (owner decision 2026-08-22).
 *
 * Tap to start, tap to stop, then LISTEN BEFORE YOU SEND. The prototype had a
 * hold-to-talk that posted on release, and that is not what is built here: a
 * knock is an assessment somebody is paying for, and a recording that sends
 * itself the instant a thumb lifts is a recording nobody heard back. The
 * preview is the whole difference, and it is not an extra step so much as the
 * point.
 *
 * A VOICE NOTE IS A MESSAGE (wave 76). Nothing here listens to it — no speech
 * API is touched in the browser. The clip goes up as a file, the server
 * transcribes it on the way in (ruling R-0905-4), and the screen that owns the
 * conversation sends it as the person's turn: the words are the transcript,
 * the bubble is the player with those words under it. A note the transcriber
 * could not hear sends nothing, and Nob asks for it typed.
 *
 * FAILURE IS A SENTENCE, NEVER A DEAD BUTTON. A browser with no MediaRecorder,
 * a denied microphone, a machine with no input — each says what happened in
 * one line, and the composer is otherwise untouched: whatever was typed is
 * still typed, and send still sends it.
 */
import { useCallback, useEffect, useRef, useState } from "react";

/** The recorder stops itself here. Long past any voice note, short of a cap. */
export const MAX_RECORDING_MS = 5 * 60_000;

/** Under this, a thumb slipped rather than a person spoke. */
const MIN_RECORDING_BYTES = 400;

export const MIC_LABEL = "Record a voice note";
export const STOP_LABEL = "Stop recording";
export const DISCARD_LABEL = "Discard the recording";
export const RECORDING_COPY = "Recording";

export const NO_RECORDER_COPY = "This browser will not record audio.";
export const NO_MIC_COPY = "Nob needs the microphone to record a voice note.";
export const TOO_SHORT_COPY = "That was too short to send.";

/**
 * Nob's one line when a note came back with no transcript (wave 76): nothing
 * was sent as a turn, the note stays in the thread as a file, and there is no
 * retry — a second attempt at the same bytes hears the same nothing.
 */
export const VOICE_UNHEARD_COPY = "I couldn't make out that voice note. Could you type it instead?";

/**
 * What we ask the recorder for, in the order we would rather have it.
 *
 * Opus in WebM is what Chrome and Firefox produce and what the API's allowlist
 * names first; Safari has no WebM and answers with MP4/AAC. `isTypeSupported`
 * is missing altogether on some older builds, which is not a reason to refuse
 * to record — an empty choice means "you pick", and the recorder's own default
 * is then read back off the instance.
 */
const PREFERRED = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"];

export function pickRecorderMime(): string {
  const supports = (globalThis as { MediaRecorder?: typeof MediaRecorder }).MediaRecorder;
  if (!supports?.isTypeSupported) return "";
  return PREFERRED.find((type) => supports.isTypeSupported(type)) ?? "";
}

/**
 * The filename the upload carries, derived from the recorder's own type.
 *
 * The API judges a file by its EXTENSION, so this is not cosmetic — it is the
 * thing that decides whether the upload is understood as audio at all. Two
 * extensions, matching the two types a browser can produce.
 */
export function recordingFilename(mime: string): string {
  return mime.includes("mp4") ? "voice-note.m4a" : "voice-note.webm";
}

export type Recording = {
  blob: Blob;
  /** An object URL for the preview player. Revoked when the clip is dropped. */
  url: string;
  durationMs: number;
  filename: string;
};

export type VoiceRecorder = {
  /** Recording right now. */
  recording: boolean;
  /** Milliseconds so far, for the composer's counter. */
  elapsedMs: number;
  /** Recorded and waiting to be sent or dropped. */
  clip: Recording | null;
  /** What went wrong, in one sentence, or null. */
  error: string | null;
  start: () => void;
  stop: () => void;
  /** Throw the clip away and free its object URL. */
  discard: () => void;
  /** Clear the sentence — the composer does this when the sender moves on. */
  clearError: () => void;
};

/** Whether this browser can record at all, asked before anything is offered. */
export function canRecord(): boolean {
  const media = (globalThis as { MediaRecorder?: unknown }).MediaRecorder;
  return typeof media !== "undefined" && Boolean(navigator?.mediaDevices?.getUserMedia);
}

export function useVoiceRecorder(): VoiceRecorder {
  const [recording, setRecording] = useState(false);
  const [elapsedMs, setElapsed] = useState(0);
  const [clip, setClip] = useState<Recording | null>(null);
  const [error, setError] = useState<string | null>(null);

  const recorder = useRef<MediaRecorder | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const chunks = useRef<Blob[]>([]);
  const startedAt = useRef(0);
  const ticker = useRef(0);
  const limit = useRef(0);
  /** The last clip's URL, so a component that unmounts mid-preview frees it. */
  const url = useRef<string | null>(null);

  /**
   * Every timer and every track, put back. This is the "machinery, not memory"
   * rule applied to hardware: a microphone left open is a light left on on
   * somebody's laptop, and it must not depend on which path the code took out.
   */
  const release = useCallback(() => {
    window.clearInterval(ticker.current);
    window.clearTimeout(limit.current);
    stream.current?.getTracks().forEach((track) => track.stop());
    stream.current = null;
    recorder.current = null;
  }, []);

  useEffect(() => {
    return () => {
      const active = recorder.current;
      if (active && active.state !== "inactive") {
        try {
          active.stop();
        } catch {
          /* already gone */
        }
      }
      release();
      if (url.current) URL.revokeObjectURL(url.current);
    };
  }, [release]);

  const stop = useCallback(() => {
    const active = recorder.current;
    if (!active || active.state === "inactive") {
      release();
      setRecording(false);
      return;
    }
    try {
      active.stop();
    } catch {
      release();
      setRecording(false);
    }
  }, [release]);

  const discard = useCallback(() => {
    setClip(null);
    if (url.current) URL.revokeObjectURL(url.current);
    url.current = null;
  }, []);

  const start = useCallback(() => {
    if (recorder.current) return;
    setError(null);
    if (!canRecord()) {
      setError(NO_RECORDER_COPY);
      return;
    }
    // A clip already waiting is replaced by the new one: two takes of the same
    // thought is one thought, and the first was already rejected by re-recording.
    discard();

    void (async () => {
      let media: MediaStream;
      try {
        media = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch {
        // Denied, dismissed, or no input device. All the same sentence: the
        // sender's move is identical in each case, and naming which would be
        // telling them about their own hardware.
        setError(NO_MIC_COPY);
        return;
      }

      const mime = pickRecorderMime();
      let active: MediaRecorder;
      try {
        active = mime ? new MediaRecorder(media, { mimeType: mime }) : new MediaRecorder(media);
      } catch {
        media.getTracks().forEach((track) => track.stop());
        setError(NO_RECORDER_COPY);
        return;
      }

      stream.current = media;
      recorder.current = active;
      chunks.current = [];
      startedAt.current = Date.now();
      setElapsed(0);

      active.ondataavailable = (event) => {
        if (event.data.size) chunks.current.push(event.data);
      };
      active.onstop = () => {
        const durationMs = Date.now() - startedAt.current;
        // `active.mimeType` over the requested one: a recorder handed no type
        // picked its own, and the extension has to match what it actually wrote.
        const type = active.mimeType || mime || "audio/webm";
        const blob = new Blob(chunks.current, { type });
        chunks.current = [];
        release();
        setRecording(false);
        setElapsed(0);
        if (blob.size < MIN_RECORDING_BYTES) {
          setError(TOO_SHORT_COPY);
          return;
        }
        const objectUrl = URL.createObjectURL(blob);
        url.current = objectUrl;
        setClip({ blob, url: objectUrl, durationMs, filename: recordingFilename(type) });
      };

      try {
        active.start();
      } catch {
        release();
        setError(NO_RECORDER_COPY);
        return;
      }

      setRecording(true);
      ticker.current = window.setInterval(() => {
        setElapsed(Date.now() - startedAt.current);
      }, 200);
      // The recorder stops itself rather than running until the tab closes.
      limit.current = window.setTimeout(stop, MAX_RECORDING_MS);
    })();
  }, [discard, release, stop]);

  const clearError = useCallback(() => setError(null), []);

  return { recording, elapsedMs, clip, error, start, stop, discard, clearError };
}
